import * as UI from "@iyk/ui"
import * as React from "react"

import { Carousel } from "@iyk/vf-ui"
import type { Profile } from "../profile.ts"

const Root = Carousel.Root

const Container = ({ children }: CollectionCarousel.Container) => {
  const { scrollSnapList } = Carousel.useCarousel()
  const showContainer = Boolean(scrollSnapList().length)

  return (
    <div
      data-show={showContainer}
      className="px-4 flex flex-col justify-center relative opacity-0 data-[show=true]:opacity-100 transition-opacity duration-300 overflow-hidden"
      data-role="carousel-container"
    >
      {children}
    </div>
  )
}

const Slides = ({ children, data }: CollectionCarousel.Slides) => {
  const { ref } = Carousel.useCarousel()

  return (
    <div ref={ref} className="flex flex-col" data-role="carousel-slides-viewport">
      <div className="flex -ml-6 [touch-action:pan-y_pinch-zoom]" data-role="carousel-slides-container">
        {data.map((collection, index) => (
          <Slide key={collection.id} index={index}>
            {children(collection)}
          </Slide>
        ))}
      </div>
    </div>
  )
}

const Slide = ({ children, index }: CollectionCarousel.Slide) => {
  const { selectedScrollSnap, onSlideClick } = Carousel.useCarousel()
  const isSnapped = index !== selectedScrollSnap()
  return (
    <section
      data-snapped={isSnapped}
      onClick={() => onSlideClick?.(index)}
      className={UI.cx([
        "flex flex-col justify-center min-w-0 pl-6 select-none",
        "[transform:translate3d(0,0,0)]",
        "flex-[0_0_100%] cursor-pointer",
        "transition-all duration-300 animate-in"
      ])}
    >
      {children}
    </section>
  )
}

const DRAG_THRESHOLD = 12 // padding around a line
const Lines = () => {
  const { goTo, selectedScrollSnap, scrollSnapList } = Carousel.useCarousel()
  const [isDragging, setIsDragging] = React.useState(false)
  const startXRef = React.useRef<number>(0)

  const handleDragStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    startXRef.current = e.touches[0].clientX
  }

  const handleDragMove = (e: React.TouchEvent) => {
    if (!isDragging) return

    const currentX = e.touches[0].clientX
    const deltaX = currentX - startXRef.current

    if (Math.abs(deltaX) >= DRAG_THRESHOLD) {
      const direction = deltaX > 0 ? 1 : -1
      const currentIndex = selectedScrollSnap()
      const maxIndex = scrollSnapList().length - 1
      const newIndex = Math.max(0, Math.min(currentIndex + direction, maxIndex))

      goTo(newIndex)
      startXRef.current = currentX
    }
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  const lines = scrollSnapList()
  const showLines = lines.length > 1
  const selectedLine = selectedScrollSnap()

  const isAdjacent = (index: number) => {
    return Math.abs(index - selectedLine) === 1
  }

  return (
    <div
      className="h-5 flex items-end justify-center relative touch-none select-none data-[show=true]:opacity-100 opacity-0 transition-opacity duration-300"
      data-role="carousel-dots"
      data-show={showLines}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      {lines.map((_, index) => (
        <div role="button" onClick={() => goTo(index)} className="px-1.5 bg-transparent" key={index}>
          <div
            data-dragging={isDragging}
            data-adjacent={isAdjacent(index)}
            data-active={selectedLine === index}
            className={UI.cx([
              "h-4 w-px bg-white opacity-30",
              "data-[active=true]:h-5 data-[active=true]:opacity-100",
              "data-[dragging=true]:data-[active=true]:h-[22px]",
              "data-[dragging=true]:data-[adjacent=true]:h-5 data-[dragging=true]:data-[adjacent=true]:opacity-50",
              "[transition:opacity_150ms,height_300ms] ease-in-out"
            ])}
          />
        </div>
      ))}
    </div>
  )
}

namespace CollectionCarousel {
  export type Container = {
    children: React.ReactNode
  }

  export type Slides = {
    children: (collectable: Profile.Collectable) => React.ReactNode
    data: Array<Profile.Collectable>
  }

  export type Slide = {
    children: React.ReactNode
    index: number
  }
}

export const CollectionCarousel = {
  Root,
  Container,
  Slides,
  Slide,
  Lines
}
